import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cdnPath } from "@/utility/api";

const companies = [
  { name: "DELOITTE", logo: `${cdnPath}/logos/Deloitte_idUZmqOXQ3_1.png` },
  { name: "KPMG", logo: `${cdnPath}/logos/kpmg-seeklogo.png` },
  { name: "EY", logo: `${cdnPath}/logos/EY_idKDHDpz8E_1.png` },
  {
    name: "GRANT THORNTON",
    logo: `${cdnPath}/logos/Grant Thornton_idEvbVQe3L_1.png`,
  },
  {
    name: "BAIN & COMPANY",
    logo: `${cdnPath}/logos/Bain & Company_idUdrpBxE3_1.png`,
  },
  { name: "ZOMATO", logo: `${cdnPath}/logos/zomato-seeklogo.png` },
  { name: "SWIGGY", logo: `${cdnPath}/logos/Swiggy_id8bItcgXR_1.png` },
  { name: "BLINKIT", logo: `${cdnPath}/logos/Blinkit_idCmcpCDCZ_1.png` },
  { name: "CARS24", logo: `${cdnPath}/logos/cars24-seeklogo.png` },
  { name: "URBAN COMPANY", logo: `${cdnPath}/logos/idt2m5clXX_logos.png` },
  {
    name: "NITI AAYOG",
    logo: `${cdnPath}/logos/niti-aayog-logo-vector-768x427.png`,
  },
  { name: "INVEST INDIA", logo: `${cdnPath}/logos/invesindialogo.jpg` },
  { name: "FICCI", logo: `${cdnPath}/logos/ficilogo.jpg` },
  { name: "UNSTOP", logo: `${cdnPath}/logos/Unstop_idGARQA_PG_0.png` },
  { name: "TATA 1MG", logo: `${cdnPath}/logos/tata1mg.png` },
  { name: "ARBOLUS", logo: `${cdnPath}/logos/arbo.jpg` },
  { name: "GLG", logo: `${cdnPath}/logos/GLG_idZfDW2w6l_1.png` },
  { name: "ZEPTO", logo: `${cdnPath}/logos/zepto-seeklogo.png` },
  { name: "MAGICPIN", logo: `${cdnPath}/logos/magicpin-logo-768x203.png` },
  { name: "REALME", logo: `${cdnPath}/logos/Realme Global_idaY33QoY7_0.png` },
];

// third row content: job roles
const jobRoles = [
  "Founding Team Sales Lead",
  "GTM",
  "Founding BDR's",
  "Founder's Office",
  "Growth and Revenue",
  "Demand & Growth Specialist",
];

// split into two rows
const firstRowCompanies = companies.slice(0, Math.ceil(companies.length / 2));
const secondRowCompanies = companies.slice(Math.ceil(companies.length / 2));

// duplicate for seamless loop
const firstRowList = [...firstRowCompanies, ...firstRowCompanies];
const secondRowList = [...secondRowCompanies.reverse(), ...secondRowCompanies];
const jobRolesList = [...jobRoles, ...jobRoles];

export default function CompanyLogoCarousel() {
  const reduced = useReducedMotion();

  const marqueeLTR = {
    animate: { translateX: ["0%", "-50%"] },
    transition: {
      translateX: {
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "loop",
        duration: 60,
        ease: "linear",
      },
    },
  };

  const marqueeRTL = {
    animate: { translateX: ["-50%", "0%"] },
    transition: {
      translateX: {
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "loop",
        duration: 55,
        ease: "linear",
      },
    },
  };

  return (
    <section
      className="w-full py-10 relative overflow-hidden md:mt-20"
      aria-labelledby="students-placed-at"
    >
      {/* Global fade overlays */}
      <div
        className="absolute left-0 top-0 w-20 h-full 
                      bg-gradient-to-r from-black to-transparent z-20 pointer-events-none"
      />
      <div
        className="absolute right-0 top-0 w-20 h-full 
                      bg-gradient-to-l from-black to-transparent z-20 pointer-events-none"
      />

      {/* widen container so rows take more width */}
      <div className="max-w-8xl mx-auto px-4 relative z-10">
        <h2
          id="students-placed-at"
          className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-8 text-center"
        >
          Students placed at
        </h2>

        <div className="space-y-6">
          {/* Row 1 */}
          <div className="w-full overflow-hidden mb-0 md:mb-8">
            <motion.div
              className="flex w-max items-center gap-12 px-2"
              {...(!reduced
                ? {
                    animate: marqueeLTR.animate,
                    transition: marqueeLTR.transition,
                  }
                : { style: { transform: "translateX(0%)" } })}
            >
              {firstRowList.map((company, index) => (
                <div
                  key={index}
                  className="flex-none w-36"
                  aria-label={company.name}
                >
                  <div className="h-16 flex items-center justify-center">
                    <img
                      src={company.logo}
                      alt={`${company.name} logo`}
                      className="object-contain w-24 h-8"
                    />
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Row 2 */}
          <div className="w-full overflow-hidden">
            <motion.div
              className="flex w-max items-center gap-8 px-2"
              {...(!reduced
                ? {
                    animate: marqueeRTL.animate,
                    transition: marqueeRTL.transition,
                  }
                : { style: { transform: "translateX(0%)" } })}
            >
              {secondRowList.map((company, index) => (
                <div
                  key={index}
                  className="flex-none w-32"
                  aria-label={company.name}
                >
                  <div className="h-16 flex items-center justify-center">
                    <img
                      src={company.logo}
                      alt={`${company.name} logo`}
                      className="object-contain w-24 h-8"
                    />
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
